import React, { useState, useMemo, useCallback } from 'react';
import GenericButton from '@ui-components/Button/GenericButton';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import { epData, typeColors } from '../backbone.data';
import { Connector, ConnectorType } from '../backbone.types';

/* ═══════════════════════════════════════════════════════════
   Traffic Policy Matrix — scalable group-to-group + detail
   ───────────────────────────────────────────────────────────
   Top: compact 3×3 group matrix  (Cloud / SD-WAN / Port)
   Bottom: expanded detail for selected group pair
   ═══════════════════════════════════════════════════════════ */

type CellState = 'none' | 'allow' | 'deny';

const cellKey = (from: string, to: string) =>
    `${from}\u2192${to}`;

const epLabel = (ep: Connector): string =>
    ep.type === 'Cloud' && ep.provider
        ? `Cloud (${ep.provider})`
        : ep.label;

/* ── Group definitions ── */
const GROUPS: { type: ConnectorType; label: string; icon: string }[] =
    [
        { type: 'Cloud', label: 'Cloud aaS', icon: '\u2601\uFE0F' },
        { type: 'SDhub', label: 'SDHub aaS', icon: '\uD83C\uDF10' },
        { type: 'Port', label: 'Physical Port', icon: '\uD83D\uDD0C' },
    ];

/* ── Seed data ── */
const seedMatrix = (): Record<string, CellState> => {
    const m: Record<string, CellState> = {};
    m[cellKey('EP-003', 'EP-001')] = 'allow';
    m[cellKey('EP-005', 'EP-008')] = 'allow';
    m[cellKey('EP-006', 'EP-001')] = 'deny';
    m[cellKey('EP-006', 'EP-002')] = 'deny';
    m[cellKey('EP-002', 'EP-004')] = 'allow';
    m[cellKey('EP-009', 'EP-007')] = 'allow';
    m[cellKey('EP-001', 'EP-003')] = 'allow';
    m[cellKey('EP-008', 'EP-005')] = 'allow';
    return m;
};

const nextState = (s: CellState): CellState => {
    if (s === 'none') return 'allow';
    if (s === 'allow') return 'deny';
    return 'none';
};

/* ── Styling helpers ── */
const cellClasses = (s: CellState): string => {
    if (s === 'allow')
        return 'bg-semantic-bg-success-secondary text-semantic-text-success border-semantic-border-success-primary';
    if (s === 'deny')
        return 'bg-semantic-bg-danger-secondary text-semantic-text-danger border-semantic-border-danger-primary';
    return 'bg-semantic-bg-secondary text-semantic-text-secondary border-semantic-border-primary';
};

const cellSymbol = (s: CellState): string => {
    if (s === 'allow') return '\u2713';
    if (s === 'deny') return '\u2717';
    return '\u2014';
};

/* ═══════════════════════════════════════════════════════════ */
interface PolicyRulesTabProps {
    vrfFilterSelection?: string[];
}

const PolicyRulesTab: React.FC<PolicyRulesTabProps> = ({ vrfFilterSelection = [] }) => {
    const [matrix, setMatrix] = useState<
        Record<string, CellState>
    >(seedMatrix);

    /* Currently selected group pair for detail view */
    const [selectedPair, setSelectedPair] = useState<{
        from: ConnectorType;
        to: ConnectorType;
    } | null>(null);

    /* Connectors grouped by type */
    const grouped = useMemo(() => {
        const data =
            vrfFilterSelection.length === 0
                ? epData
                : epData.filter(
                      (e) => e.vrf != null && vrfFilterSelection.includes(e.vrf)
                  );
        const map: Record<ConnectorType, Connector[]> = {
            Cloud: [],
            SDhub: [],
            Port: [],
            Fwaas: [],
            Sdwan: [],
        };
        data.forEach((ep) => map[ep.type].push(ep));
        return map;
    }, [vrfFilterSelection]);

    /* ── Group-level summary ── */
    const groupSummary = useCallback(
        (
            fromType: ConnectorType,
            toType: ConnectorType
        ): {
            allow: number;
            deny: number;
            total: number;
        } => {
            const froms = grouped[fromType];
            const tos = grouped[toType];
            let allow = 0;
            let deny = 0;
            let total = 0;
            froms.forEach((f) =>
                tos.forEach((t) => {
                    if (f.id === t.id) return;
                    total++;
                    const s = matrix[cellKey(f.id, t.id)];
                    if (s === 'allow') allow++;
                    if (s === 'deny') deny++;
                })
            );
            return { allow, deny, total };
        },
        [grouped, matrix]
    );

    /* ── Cell toggle ── */
    const toggleCell = useCallback(
        (from: string, to: string) => {
            const key = cellKey(from, to);
            setMatrix((prev) => {
                const current = prev[key] ?? 'none';
                const ns = nextState(current);
                const copy = { ...prev };
                if (ns === 'none') delete copy[key];
                else copy[key] = ns;
                return copy;
            });
        },
        []
    );

    /* ── Set all pairs in a group block ── */
    const setGroupBlock = useCallback(
        (
            fromType: ConnectorType,
            toType: ConnectorType,
            state: CellState
        ) => {
            setMatrix((prev) => {
                const copy = { ...prev };
                grouped[fromType].forEach((f) =>
                    grouped[toType].forEach((t) => {
                        if (f.id === t.id) return;
                        const key = cellKey(f.id, t.id);
                        if (state === 'none')
                            delete copy[key];
                        else copy[key] = state;
                    })
                );
                return copy;
            });
        },
        [grouped]
    );

    /* ── Global stats ── */
    const stats = useMemo(() => {
        const vals = Object.values(matrix);
        return {
            allow: vals.filter((v) => v === 'allow').length,
            deny: vals.filter((v) => v === 'deny').length,
        };
    }, [matrix]);

    /* ── Detail connectors for selected pair ── */
    const detailFroms = selectedPair
        ? grouped[selectedPair.from]
        : [];
    const detailTos = selectedPair
        ? grouped[selectedPair.to]
        : [];

    const isSameGroup =
        selectedPair?.from === selectedPair?.to;

    return (
        <>
            {/* ═══════ Header ═══════ */}
            <div className="mt-4 rounded-md border border-gray-300 bg-semantic-bg-primary">
                <div className="px-6 py-3 border-b flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <h2 className="text-md font-bold text-semantic-text-primary">
                            Traffic Policy Matrix
                        </h2>
                        <span className="inline-flex items-center rounded-full bg-semantic-bg-warning-secondary px-2.5 py-0.5 text-xs font-semibold text-semantic-text-warning">
                            BETA
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-4 text-xs text-semantic-text-secondary">
                            <span className="flex items-center gap-1">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-semantic-bg-success-secondary border border-semantic-border-success-primary" />
                                {stats.allow} allowed
                            </span>
                            <span className="flex items-center gap-1">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-semantic-bg-danger-secondary border border-semantic-border-danger-primary" />
                                {stats.deny} denied
                            </span>
                        </div>
                        <GenericButton
                            variant="secondary"
                            size="sm"
                            onClick={() => setMatrix({})}
                        >
                            Reset All
                        </GenericButton>
                    </div>
                </div>

                {/* Instructions */}
                <div className="px-6 py-3 bg-semantic-bg-secondary border-b">
                    <p className="text-xs text-semantic-text-secondary leading-relaxed">
                        Select a group block below to manage
                        individual connector-to-connector
                        policies, or use the quick-set buttons to
                        allow/deny all traffic in a block at once.
                    </p>
                </div>

                {/* ═══════ Group Matrix (3×3) ═══════ */}
                <div className="p-5">
                    <div className="inline-block">
                        <table className="border-collapse">
                            <thead>
                                <tr>
                                    <th className="p-2 min-w-[100px]">
                                        <span className="text-[10px] text-semantic-text-secondary">
                                            FROM &#x2193; / TO
                                            &#x2192;
                                        </span>
                                    </th>
                                    {GROUPS.map((g) => (
                                        <th
                                            key={g.type}
                                            className="p-2 text-center min-w-[140px]"
                                        >
                                            <span className="text-base">
                                                {g.icon}
                                            </span>
                                            <div
                                                className="text-xs font-semibold mt-0.5"
                                                style={{
                                                    color: typeColors[
                                                        g.type
                                                    ].text,
                                                }}
                                            >
                                                {g.label}
                                            </div>
                                            <div className="text-[10px] text-semantic-text-secondary">
                                                {
                                                    grouped[
                                                        g.type
                                                    ].length
                                                }{' '}
                                                connectors
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {GROUPS.map((rowG) => (
                                    <tr key={rowG.type}>
                                        <td className="p-2 border-t border-semantic-border-primary">
                                            <div className="flex items-center gap-2">
                                                <span className="text-base">
                                                    {rowG.icon}
                                                </span>
                                                <div>
                                                    <div
                                                        className="text-xs font-semibold"
                                                        style={{
                                                            color: typeColors[
                                                                rowG
                                                                    .type
                                                            ]
                                                                .text,
                                                        }}
                                                    >
                                                        {
                                                            rowG.label
                                                        }
                                                    </div>
                                                    <div className="text-[10px] text-semantic-text-secondary">
                                                        {
                                                            grouped[
                                                                rowG
                                                                    .type
                                                            ]
                                                                .length
                                                        }{' '}
                                                        connectors
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        {GROUPS.map((colG) => {
                                            const summary =
                                                groupSummary(
                                                    rowG.type,
                                                    colG.type
                                                );
                                            const isSelected =
                                                selectedPair?.from ===
                                                    rowG.type &&
                                                selectedPair?.to ===
                                                    colG.type;

                                            return (
                                                <td
                                                    key={
                                                        colG.type
                                                    }
                                                    className="p-2 border-t border-semantic-border-primary"
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setSelectedPair(
                                                                isSelected
                                                                    ? null
                                                                    : {
                                                                          from: rowG.type,
                                                                          to: colG.type,
                                                                      }
                                                            )
                                                        }
                                                        className={`w-full rounded-lg border-2 p-3 transition-all cursor-pointer hover:shadow-md ${
                                                            isSelected
                                                                ? 'border-semantic-border-brand-primary bg-semantic-bg-brand-secondary shadow-sm'
                                                                : 'border-semantic-border-primary bg-semantic-bg-primary hover:border-semantic-border-secondary'
                                                        }`}
                                                    >
                                                        {/* Mini bars */}
                                                        <div className="flex gap-0.5 h-2 rounded overflow-hidden mb-2">
                                                            {summary.total >
                                                            0 ? (
                                                                <>
                                                                    {summary.allow >
                                                                        0 && (
                                                                        <div
                                                                            className="bg-semantic-bg-success-secondary rounded-sm"
                                                                            style={{
                                                                                width: `${(summary.allow / summary.total) * 100}%`,
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {summary.deny >
                                                                        0 && (
                                                                        <div
                                                                            className="bg-semantic-bg-danger-secondary rounded-sm"
                                                                            style={{
                                                                                width: `${(summary.deny / summary.total) * 100}%`,
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {summary.total -
                                                                        summary.allow -
                                                                        summary.deny >
                                                                        0 && (
                                                                        <div
                                                                            className="bg-semantic-bg-tertiary rounded-sm flex-1"
                                                                        />
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div className="bg-semantic-bg-tertiary rounded-sm flex-1" />
                                                            )}
                                                        </div>

                                                        {/* Counts */}
                                                        <div className="flex items-center justify-center gap-3 text-[11px]">
                                                            <span className="text-semantic-text-success font-semibold">
                                                                {
                                                                    summary.allow
                                                                }
                                                                <span className="font-normal text-semantic-text-secondary ml-0.5">
                                                                    &#x2713;
                                                                </span>
                                                            </span>
                                                            <span className="text-semantic-text-danger font-semibold">
                                                                {
                                                                    summary.deny
                                                                }
                                                                <span className="font-normal text-semantic-text-secondary ml-0.5">
                                                                    &#x2717;
                                                                </span>
                                                            </span>
                                                            <span className="text-semantic-text-secondary">
                                                                /
                                                                {
                                                                    summary.total
                                                                }
                                                            </span>
                                                        </div>

                                                        {/* Quick-set */}
                                                        <div className="flex justify-center gap-1 mt-2">
                                                            <button
                                                                type="button"
                                                                onClick={(
                                                                    e
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    setGroupBlock(
                                                                        rowG.type,
                                                                        colG.type,
                                                                        'allow'
                                                                    );
                                                                }}
                                                                className="px-2 py-0.5 rounded text-[10px] font-medium bg-semantic-bg-success-secondary text-semantic-text-success border border-semantic-border-success-primary hover:opacity-80 transition-opacity"
                                                                title="Allow all"
                                                            >
                                                                Allow
                                                                All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={(
                                                                    e
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    setGroupBlock(
                                                                        rowG.type,
                                                                        colG.type,
                                                                        'deny'
                                                                    );
                                                                }}
                                                                className="px-2 py-0.5 rounded text-[10px] font-medium bg-semantic-bg-danger-secondary text-semantic-text-danger border border-semantic-border-danger-primary hover:opacity-80 transition-opacity"
                                                                title="Deny all"
                                                            >
                                                                Deny
                                                                All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={(
                                                                    e
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    setGroupBlock(
                                                                        rowG.type,
                                                                        colG.type,
                                                                        'none'
                                                                    );
                                                                }}
                                                                className="px-2 py-0.5 rounded text-[10px] font-medium bg-semantic-bg-secondary text-semantic-text-secondary border border-semantic-border-primary hover:opacity-80 transition-opacity"
                                                                title="Clear"
                                                            >
                                                                Clear
                                                            </button>
                                                        </div>
                                                    </button>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* ═══════ Detail Matrix (expanded group pair) ═══════ */}
            {selectedPair && detailFroms.length > 0 && detailTos.length > 0 && (
                <div className="mt-4 rounded-md border border-gray-300 bg-semantic-bg-primary">
                    <div className="px-6 py-3 border-b flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span
                                className="text-sm font-semibold"
                                style={{
                                    color: typeColors[
                                        selectedPair.from
                                    ].text,
                                }}
                            >
                                {
                                    GROUPS.find(
                                        (g) =>
                                            g.type ===
                                            selectedPair.from
                                    )?.label
                                }
                            </span>
                            <span className="text-semantic-text-secondary">
                                &#x2192;
                            </span>
                            <span
                                className="text-sm font-semibold"
                                style={{
                                    color: typeColors[
                                        selectedPair.to
                                    ].text,
                                }}
                            >
                                {
                                    GROUPS.find(
                                        (g) =>
                                            g.type ===
                                            selectedPair.to
                                    )?.label
                                }
                            </span>
                            <span className="text-xs text-semantic-text-secondary ml-2">
                                Connector-level policies
                            </span>
                        </div>
                        <GenericButton
                            variant="secondary"
                            size="xsm"
                            onClick={() =>
                                setSelectedPair(null)
                            }
                        >
                            Close
                        </GenericButton>
                    </div>

                    <div className="p-4 overflow-x-auto">
                        <table className="border-collapse">
                            <thead>
                                <tr>
                                    <th className="p-2 min-w-[140px] sticky left-0 z-10 bg-semantic-bg-primary">
                                        <span className="text-[10px] text-semantic-text-secondary">
                                            SOURCE &#x2193; /
                                            DEST &#x2192;
                                        </span>
                                    </th>
                                    {detailTos.map((ep) => (
                                        <th
                                            key={ep.id}
                                            className="p-1.5 min-w-[72px] text-center"
                                        >
                                            <span
                                                className="text-[10px] font-medium block"
                                                style={{
                                                    color: typeColors[
                                                        ep.type
                                                    ].text,
                                                }}
                                            >
                                                {epLabel(ep)}
                                            </span>
                                            <span className="text-[9px] text-semantic-text-secondary block">
                                                {ep.loc}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {detailFroms.map((fromEp) => (
                                    <tr key={fromEp.id}>
                                        <td className="p-2 border-t border-semantic-border-primary sticky left-0 z-10 bg-semantic-bg-primary">
                                            <span
                                                className="text-xs font-medium"
                                                style={{
                                                    color: typeColors[
                                                        fromEp
                                                            .type
                                                    ].text,
                                                }}
                                            >
                                                {epLabel(
                                                    fromEp
                                                )}
                                            </span>
                                            <span className="text-[9px] text-semantic-text-secondary block">
                                                {fromEp.loc}
                                            </span>
                                        </td>
                                        {detailTos.map(
                                            (toEp) => {
                                                const isSelf =
                                                    fromEp.id ===
                                                    toEp.id;
                                                const key =
                                                    cellKey(
                                                        fromEp.id,
                                                        toEp.id
                                                    );
                                                const state: CellState =
                                                    matrix[
                                                        key
                                                    ] ?? 'none';

                                                if (
                                                    isSelf &&
                                                    isSameGroup
                                                ) {
                                                    return (
                                                        <td
                                                            key={
                                                                toEp.id
                                                            }
                                                            className="p-1 border-t border-semantic-border-primary"
                                                        >
                                                            <div className="w-full h-9 rounded bg-semantic-bg-tertiary flex items-center justify-center">
                                                                <span className="text-[10px] text-semantic-text-secondary">
                                                                    &#x2014;
                                                                </span>
                                                            </div>
                                                        </td>
                                                    );
                                                }

                                                return (
                                                    <td
                                                        key={
                                                            toEp.id
                                                        }
                                                        className="p-1 border-t border-semantic-border-primary"
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                toggleCell(
                                                                    fromEp.id,
                                                                    toEp.id
                                                                )
                                                            }
                                                            className={`w-full h-9 rounded border text-sm font-bold transition-all hover:scale-105 active:scale-95 cursor-pointer ${cellClasses(state)}`}
                                                            title={`${epLabel(fromEp)} \u2192 ${epLabel(toEp)}: ${state}`}
                                                        >
                                                            {cellSymbol(
                                                                state
                                                            )}
                                                        </button>
                                                    </td>
                                                );
                                            }
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ═══════ Legend ═══════ */}
            <div className="mt-4 rounded-md border border-gray-300 bg-semantic-bg-primary">
                <div className="py-3 px-6 flex flex-wrap gap-6 items-center">
                    <span className="text-xs font-medium text-semantic-text-secondary">
                        Legend:
                    </span>
                    <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded border text-xs font-bold bg-semantic-bg-success-secondary text-semantic-text-success border-semantic-border-success-primary">
                            &#x2713;
                        </span>
                        <span className="text-xs text-semantic-text-secondary">
                            Allowed
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded border text-xs font-bold bg-semantic-bg-danger-secondary text-semantic-text-danger border-semantic-border-danger-primary">
                            &#x2717;
                        </span>
                        <span className="text-xs text-semantic-text-secondary">
                            Denied
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded border text-xs font-bold bg-semantic-bg-secondary text-semantic-text-secondary border-semantic-border-primary">
                            &#x2014;
                        </span>
                        <span className="text-xs text-semantic-text-secondary">
                            Undefined
                        </span>
                    </div>
                </div>
            </div>

            {/* ═══════ Active Rules Summary ═══════ */}
            {(stats.allow > 0 || stats.deny > 0) && (
                <div className="mt-4 rounded-md border border-gray-300 bg-semantic-bg-primary">
                    <h2 className="text-md px-6 py-2 font-bold text-semantic-text-primary text-left border-b">
                        Active Rules ({stats.allow + stats.deny})
                    </h2>
                    <div className="p-4 max-h-[240px] overflow-y-auto">
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(matrix).map(
                                ([k, state]) => {
                                    const parts =
                                        k.split('\u2192');
                                    const fromEp =
                                        epData.find(
                                            (e) =>
                                                e.id ===
                                                parts[0]
                                        );
                                    const toEp = epData.find(
                                        (e) =>
                                            e.id === parts[1]
                                    );
                                    if (!fromEp || !toEp)
                                        return null;

                                    return (
                                        <div
                                            key={k}
                                            className="inline-flex items-center gap-1.5 rounded-full border bg-semantic-bg-secondary px-3 py-1"
                                        >
                                            <GenericTag
                                                label={
                                                    state ===
                                                    'allow'
                                                        ? 'Allow'
                                                        : 'Deny'
                                                }
                                                variant={
                                                    state ===
                                                    'allow'
                                                        ? 'success'
                                                        : 'error'
                                                }
                                                size="small"
                                            />
                                            <span className="text-xs text-semantic-text-primary font-medium">
                                                {epLabel(
                                                    fromEp
                                                )}
                                            </span>
                                            <span className="text-xs text-semantic-text-secondary">
                                                &#x2192;
                                            </span>
                                            <span className="text-xs text-semantic-text-primary font-medium">
                                                {epLabel(toEp)}
                                            </span>
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PolicyRulesTab;
